/**
 * ErvaPOS - Products Management Page
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Package,
  Loader2,
  Save,
  X,
  Eye,
  ExternalLink
} from 'lucide-react';
import { productsAPI, providersAPI, suppliersAPI } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const initialFormState = {
  name: '',
  description: '',
  barcode: '',
  category: '',
  cost_price: 0,
  sell_price: 0,
  vat_rate: 23,
  unit: 'un',
  min_stock: 0,
  is_service: false,
  provider_required: false,
  default_provider_id: '',
  fee_rule_type: 'none',
  fee_rule_value: 0,
  supplier_id: '',
  supplier_reference: '',
};

const normalizeText = (value = '') =>
  String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const normalizeCategoryKey = (value = '') =>
  normalizeText(value).replace(/\s+/g, ' ');

const isClinicalServiceCategory = (category = '') => {
  const key = normalizeCategoryKey(category);
  return (
    key === 'medicina' ||
    key === 'medicina dentaria' ||
    key === 'servicos' ||
    key === 'services'
  );
};

export const ProductsPage = () => {
  const { isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [selectedStock, setSelectedStock] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [historyProduct, setHistoryProduct] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openSalesHistory = async (product) => {
    setHistoryProduct(product);
    setHistoryRows([]);
    setHistoryLoading(true);
    try {
      const response = await productsAPI.getSalesHistory(product.id);
      setHistoryRows(response?.data?.movements || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao carregar histórico de vendas');
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatHistoryDate = (value) => value
    ? new Intl.DateTimeFormat('pt-PT', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
    : '—';

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const [productsRes, categoriesRes, providersRes, suppliersRes] = await Promise.all([
        productsAPI.getAll({ active_only: false }),
        productsAPI.getCategories(),
        providersAPI.getAll().catch(() => ({ data: [] })),
        suppliersAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setProducts(productsRes.data || []);
      setCategories(categoriesRes.data || []);
      setProviders(Array.isArray(providersRes?.data) ? providersRes.data : []);
      setSuppliers(Array.isArray(suppliersRes?.data) ? suppliersRes.data : []);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
    };
  const filteredProducts = useMemo(() => {
    const normalizedSearch = String(searchQuery || '').trim().toLowerCase();
  
    const filtered = products.filter((p) => {
      const matchesCategory =
        selectedCategory === 'all' ||
        normalizeCategoryKey(p.category) === normalizeCategoryKey(selectedCategory);
      const matchesStatus =
        selectedStatus === 'all' ||
        (selectedStatus === 'active' && !!p.is_active) ||
        (selectedStatus === 'inactive' && !p.is_active);
      const controlsStock = p.control_stock !== false && !p.is_service;
      const stockQuantity = Number(p.total_stock || 0);
      const matchesStock =
        selectedStock === 'all' ||
        (selectedStock === 'with_stock' && controlsStock && stockQuantity > 0) ||
        (selectedStock === 'without_stock' && controlsStock && stockQuantity <= 0);
  
      const matchesSearch =
        !normalizedSearch ||
        String(p.name || '').toLowerCase().includes(normalizedSearch) ||
        String(p.barcode || '').includes(String(searchQuery || ''));
  
      return matchesCategory && matchesStatus && matchesStock && matchesSearch;
    });
  
    const sorted = [...filtered];
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
  
    const getValue = (product) => {
      switch (sortConfig.key) {
        case 'name': return String(product.name || '').toLowerCase();
        case 'category': return String(product.category || '').toLowerCase();
        case 'cost_price': return Number(product.cost_price || 0);
        case 'sell_price': return Number(product.sell_price || 0);
        case 'margin_percent': return Number(product.margin_percent || 0);
        case 'total_stock': return Number(product.total_stock || 0);
        case 'status': return product.is_active ? 'ativo' : 'desativado';
        default: return String(product.name || '').toLowerCase();
      }
    };
  
    sorted.sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
  
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dir;
      }
  
      return String(av).localeCompare(String(bv), 'pt', { sensitivity: 'base', numeric: true }) * dir;
    });
  
    return sorted;
  }, [products, selectedCategory, selectedStatus, selectedStock, searchQuery, sortConfig]);

  const catalogueSummary = useMemo(() => {
    const activeProducts = products.filter((product) => product?.is_active !== false);
    const stockProducts = activeProducts.filter(
      (product) => product?.control_stock !== false && !product?.is_service
    );
    const hasReference = (product) => String(product?.reference || '').trim().length > 0;
    const hasStock = (product) => Number(product?.total_stock || 0) > 0;

    return {
      totalArticles: activeProducts.length,
      totalStock: stockProducts.reduce(
        (sum, product) => sum + Number(product?.total_stock || 0),
        0
      ),
      productsWithoutStock: stockProducts.filter((product) => !hasStock(product)).length,
      referencesWithStock: stockProducts.filter(
        (product) => hasReference(product) && hasStock(product)
      ).length,
      referencesWithoutStock: stockProducts.filter(
        (product) => hasReference(product) && !hasStock(product)
      ).length,
    };
  }, [products]);

  const formatCount = (value) =>
    new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 2 }).format(Number(value || 0));
  
  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };
  
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const activeProviders = (providers || []).filter((provider) => provider?.is_active !== false);

  const categoryOptions = useMemo(() => {
    const map = new Map();

    [...(categories || []), formData.category]
      .map((cat) => String(cat || '').trim())
      .filter(Boolean)
      .forEach((cat) => {
        const key = normalizeCategoryKey(cat);
        if (!map.has(key)) {
          map.set(key, cat);
        }
      });

    return Array.from(map.values()).sort((a, b) =>
      String(a).localeCompare(String(b), 'pt', { sensitivity: 'base' })
    );
  }, [categories, formData.category]);

  const isServiceMode =
    !!formData.is_service ||
    isClinicalServiceCategory(formData.category);

  const openModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description || '',
        barcode: product.barcode || '',
        category: product.category,
        cost_price: product.cost_price,
        sell_price: product.sell_price,
        vat_rate: product.vat_rate,
        unit: product.unit,
        min_stock: product.min_stock,
        is_service: !!product.is_service,
        provider_required: !!product.provider_required,
        default_provider_id: product.default_provider_id || '',
        fee_rule_type: product.fee_rule_type || 'none',
        fee_rule_value: Number(product.fee_rule_value || 0),
        supplier_id: product.supplier_id || '',
        supplier_reference: product.supplier_reference || '',
      });
    } else {
      setEditingProduct(null);
      setFormData(initialFormState);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const finalCategory = formData.category === 'new' ? newCategory : formData.category;
    if (!finalCategory || finalCategory.trim() === '') {
      toast.error('Escreva ou selecione uma categoria');
      return;
    }
    
    setSaving(true);

    try {
      const normalizedCategory = finalCategory.trim();
      const isService =
        !!formData.is_service ||
        isClinicalServiceCategory(normalizedCategory);

      const data = {
        ...formData,
        category: normalizedCategory,
        vat_rate: Number(formData.vat_rate ?? 0),
        min_stock: isService ? 0 : Number(formData.min_stock || 0),
        control_stock: isService ? false : (editingProduct ? editingProduct.control_stock !== false : true),
        control_lots: isService ? false : (editingProduct ? editingProduct.control_lots !== false : true),
        is_service: isService,
        provider_required: isService ? !!formData.provider_required : false,
        default_provider_id: isService ? (formData.default_provider_id || null) : null,
        fee_rule_type: isService ? (formData.fee_rule_type || 'none') : 'none',
        fee_rule_value: isService ? Number(formData.fee_rule_value || 0) : 0,
        supplier_id: isService ? null : (formData.supplier_id || null),
        supplier_reference: isService
          ? null
          : (formData.supplier_reference?.trim() || null),
      };

      if (!isService) {
        data.default_provider_id = null;
        data.provider_required = false;
        data.fee_rule_type = 'none';
        data.fee_rule_value = 0;
      }

      if (editingProduct) {
        await productsAPI.update(editingProduct.id, data);
        toast.success('Produto atualizado');
      } else {
        await productsAPI.create(data);
        toast.success('Produto criado');
      }

      setShowModal(false);
      loadProducts();
    } catch (error) {
      toast.error('Erro ao guardar produto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Desativar produto "${product.name}"?`)) return;

    try {
      await productsAPI.delete(product.id);
      toast.success('Produto desativado');
      loadProducts();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao desativar produto');
    }
  };

  const handleActivate = async (product) => {
    try {
      await productsAPI.update(product.id, {
        name: product.name || '',
        description: product.description || '',
        barcode: product.barcode || '',
        category: product.category || '',
        cost_price: Number(product.cost_price || 0),
        sell_price: Number(product.sell_price || 0),
        vat_rate: Number(product.vat_rate || 0),
        unit: product.unit || 'un',
        min_stock: Number(product.min_stock || 0),
        is_service: !!product.is_service,
        provider_required: !!product.provider_required,
        default_provider_id: product.default_provider_id || '',
        fee_rule_type: product.fee_rule_type || 'none',
        fee_rule_value: Number(product.fee_rule_value || 0),
        supplier_id: product.supplier_id || null,
        is_active: true,
      });
      toast.success('Produto ativado');
      loadProducts();
    } catch (error) {
      toast.error('Erro ao ativar produto');
    }
  };

  // Calculate margin when prices change
  const updateForm = (field, value) => {
    const newData = { ...formData, [field]: value };
    
    if (field === 'cost_price' || field === 'sell_price') {
      if (newData.cost_price > 0 && newData.sell_price > 0) {
        newData.margin_percent = ((newData.sell_price - newData.cost_price) / newData.cost_price * 100).toFixed(1);
      }
    }

    if (field === 'category') {
      const isClinicalCategory = isClinicalServiceCategory(value);
      if (isClinicalCategory) {
        newData.is_service = true;
        newData.min_stock = 0;
      }
    }

    if ((field === 'is_service' && !value) || (field === 'category' && !isClinicalServiceCategory(value))) {
      if (!newData.is_service && !isClinicalServiceCategory(newData.category)) {
        newData.provider_required = false;
        newData.default_provider_id = '';
        newData.fee_rule_type = 'none';
        newData.fee_rule_value = 0;
      }
    }
    
    setFormData(newData);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">Produtos</h1>
          <p className="text-slate-400">Gestão de artigos da loja</p>
        </div>
        {isManager && (
          <Button
            onClick={() => openModal()}
            className="bg-emerald-500 hover:bg-emerald-600"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Produto
          </Button>
        )}
      </div>

      {/* Catalogue summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {[
          { label: 'Total de artigos', value: catalogueSummary.totalArticles, note: 'Artigos ativos' },
          { label: 'Stock total', value: catalogueSummary.totalStock, note: 'Unidades em produtos' },
          { label: 'Produtos sem stock', value: catalogueSummary.productsWithoutStock, note: 'Artigos físicos ativos' },
          { label: 'Referências com stock', value: catalogueSummary.referencesWithStock, note: 'Com referência preenchida' },
          { label: 'Referências sem stock', value: catalogueSummary.referencesWithoutStock, note: 'Com referência preenchida' },
        ].map((item) => (
          <Card key={item.label} className="card-dark p-4">
            <p className="text-sm text-slate-400">{item.label}</p>
            <p className="mt-1 text-2xl font-bold text-white">{formatCount(item.value)}</p>
            <p className="mt-1 text-xs text-slate-500">{item.note}</p>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Pesquisar por nome ou código..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700"
            data-testid="search-products"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-48 bg-slate-800 border-slate-700" data-testid="filter-category">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as categorias</SelectItem>
            {categoryOptions.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-48 bg-slate-800 border-slate-700" data-testid="filter-status">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Desativados</SelectItem>
          </SelectContent>
        </Select>
        <Select value={selectedStock} onValueChange={setSelectedStock}>
          <SelectTrigger className="w-52 bg-slate-800 border-slate-700" data-testid="filter-stock">
            <SelectValue placeholder="Stock" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os stocks</SelectItem>
            <SelectItem value="with_stock">Apenas com stock</SelectItem>
            <SelectItem value="without_stock">Apenas sem stock</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Products Table */}
      <Card className="card-dark">
        <Table className="table-dark">
          <TableHeader>
            <TableRow>
              <TableHead><button type="button" onClick={() => handleSort("name")} className="inline-flex items-center gap-1 hover:text-white">Produto <span className="text-xs">{getSortIndicator("name")}</span></button></TableHead>
              <TableHead><button type="button" onClick={() => handleSort("category")} className="inline-flex items-center gap-1 hover:text-white">Categoria <span className="text-xs">{getSortIndicator("category")}</span></button></TableHead>
              <TableHead className="text-right"><button type="button" onClick={() => handleSort("cost_price")} className="inline-flex items-center gap-1 hover:text-white">Custo <span className="text-xs">{getSortIndicator("cost_price")}</span></button></TableHead>
              <TableHead className="text-right"><button type="button" onClick={() => handleSort("sell_price")} className="inline-flex items-center gap-1 hover:text-white">PVP <span className="text-xs">{getSortIndicator("sell_price")}</span></button></TableHead>
              <TableHead className="text-right"><button type="button" onClick={() => handleSort("margin_percent")} className="inline-flex items-center gap-1 hover:text-white">Margem <span className="text-xs">{getSortIndicator("margin_percent")}</span></button></TableHead>
              <TableHead className="text-right"><button type="button" onClick={() => handleSort("total_stock")} className="inline-flex items-center gap-1 hover:text-white">Stock <span className="text-xs">{getSortIndicator("total_stock")}</span></button></TableHead>
              <TableHead><button type="button" onClick={() => handleSort("status")} className="inline-flex items-center gap-1 hover:text-white">Estado <span className="text-xs">{getSortIndicator("status")}</span></button></TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProducts.map((product) => (
              <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                <TableCell>
                  <div>
                    <p className="font-medium text-white">{product.name}</p>
                    {product.barcode && (
                      <p className="text-xs text-slate-500">{product.barcode}</p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="border-slate-600">
                    {product.category}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">€{product.cost_price.toFixed(2)}</TableCell>
                <TableCell className="text-right font-medium text-emerald-400">
                  €{product.sell_price.toFixed(2)}
                </TableCell>
                <TableCell className="text-right">
                  <span className={product.margin_percent > 30 ? 'text-emerald-400' : product.margin_percent > 15 ? 'text-amber-400' : 'text-rose-400'}>
                    {product.margin_percent.toFixed(1)}%
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span className={product.total_stock <= product.min_stock ? 'text-rose-400' : 'text-white'}>
                    {product.total_stock}
                  </span>
                  {Number(product.draft_return_quantity || 0)>0&&<div className="text-[11px] text-amber-300">({product.draft_return_quantity} retidas em rascunho)</div>}
                </TableCell>
                <TableCell>
                  {product.is_active ? (
                    <Badge className="badge-success">Ativo</Badge>
                  ) : (
                    <Badge className="badge-danger">Desativado</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openSalesHistory(product)} title="Ver vendas do artigo">
                        <Eye className="w-4 h-4" />
                      </Button>
                      {isManager && <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openModal(product)}
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {product.is_active ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(product)}
                          className="text-rose-400 hover:text-rose-300"
                          data-testid={`delete-product-${product.id}`}
                          title="Desativar produto"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleActivate(product)}
                          className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-600/10"
                          data-testid={`activate-product-${product.id}`}
                          title="Ativar produto"
                        >
                          Ativar
                        </Button>
                      )}
                      </>}
                    </div>
                  </TableCell>
              </TableRow>
            ))}
            {filteredProducts.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum produto encontrado</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={Boolean(historyProduct)} onOpenChange={(open) => !open && setHistoryProduct(null)}>
        <DialogContent className="max-w-5xl bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle>Movimentos de stock — {historyProduct?.name}</DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-emerald-400" /></div>
          ) : (
            <div className="max-h-[60vh] overflow-auto rounded-md border border-slate-700">
              <Table className="table-dark">
                <TableHeader><TableRow>
                  <TableHead>Movimento</TableHead><TableHead>Data</TableHead><TableHead>Documento</TableHead>
                  <TableHead>Lote</TableHead><TableHead className="text-right">Antes</TableHead><TableHead className="text-right">Variação</TableHead><TableHead className="text-right">Após</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {historyRows.map((row) => <TableRow key={row.movement_id}>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{formatHistoryDate(row.occurred_at)}</TableCell>
                    <TableCell>
                      {row.movement_type === 'sale' && row.source_id ? (
                        <button type="button"
                          className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 hover:underline"
                          onClick={() => window.open(`/sales-history?sale_id=${encodeURIComponent(row.source_id)}`, '_blank')}>
                          {row.document || 'Abrir venda'}<ExternalLink className="w-3.5 h-3.5 shrink-0" />
                        </button>
                      ) : row.movement_type === 'entry' && row.source_id ? (
                        <button type="button"
                          className="inline-flex items-center gap-1 text-sky-400 hover:text-sky-300 hover:underline"
                          onClick={() => window.open(`/invoices?invoice_id=${encodeURIComponent(row.source_id)}`, '_blank')}>
                          {row.document || 'Abrir fatura'}<ExternalLink className="w-3.5 h-3.5 shrink-0" />
                        </button>
                      ) : (row.document || '—')}
                    </TableCell>
                    <TableCell>{row.lot_number || 'Sem lote'}</TableCell>
                    <TableCell className="text-right">≈ {Number(row.stock_before)}</TableCell>
                    <TableCell className={`text-right font-semibold ${Number(row.delta) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{Number(row.delta) >= 0 ? '+' : ''}{Number(row.delta)}</TableCell>
                    <TableCell className="text-right">≈ {Number(row.stock_after)}</TableCell>
                  </TableRow>)}
                  {!historyRows.length && <TableRow><TableCell colSpan={7} className="py-10 text-center text-slate-500">Ainda não existem movimentos deste artigo.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          )}
          <p className="text-xs text-slate-500">O símbolo ≈ identifica saldos históricos reconstruídos a partir dos movimentos existentes e do stock atual.</p>
        </DialogContent>
      </Dialog>

      {/* Product Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-slate-300">Nome</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => updateForm('name', e.target.value)}
                  className="bg-slate-800 border-slate-700"
                  required
                  data-testid="product-name"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Código de Barras</Label>
                <Input
                  value={formData.barcode}
                  onChange={(e) => updateForm('barcode', e.target.value)}
                  className="bg-slate-800 border-slate-700"
                  data-testid="product-barcode"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Categoria</Label>
                <Select
                  value={formData.category}
                  onValueChange={(val) => {
                    if (val === 'new') {
                      updateForm('category', 'new');
                      setNewCategory('');
                    } else {
                      updateForm('category', val);
                      setNewCategory('');
                    }
                  }}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="product-category">
                    <SelectValue placeholder="Selecionar categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                    <SelectItem value="new">+ Nova categoria</SelectItem>
                  </SelectContent>
                </Select>
                {formData.category === 'new' && (
                  <Input
                    placeholder="Nome da nova categoria"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="mt-2 bg-slate-800 border-slate-700"
                    data-testid="new-category"
                  />
                )}
              </div>

              <div>
                <Label className="text-slate-300">Preço de Custo</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cost_price}
                  onChange={(e) => updateForm('cost_price', parseFloat(String(e.target.value).replace(',', '.')) || 0)}
                  className="bg-slate-800 border-slate-700"
                  data-testid="product-cost"
                />
              </div>

              <div>
                <Label className="text-slate-300">Preço de Venda (PVP)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.sell_price}
                  onChange={(e) => updateForm('sell_price', parseFloat(String(e.target.value).replace(',', '.')) || 0)}
                  className="bg-slate-800 border-slate-700"
                  data-testid="product-price"
                />
              </div>

              <div>
                <Label className="text-slate-300">Taxa IVA (%)</Label>
                <Select
                  value={formData.vat_rate.toString()}
                  onValueChange={(val) => updateForm('vat_rate', parseFloat(val))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="product-vat">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% - Isento</SelectItem>
                    <SelectItem value="6">6% - Reduzida</SelectItem>
                    <SelectItem value="13">13% - Intermédia</SelectItem>
                    <SelectItem value="23">23% - Normal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-300">Stock Mínimo</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.min_stock}
                  onChange={(e) => updateForm('min_stock', parseInt(e.target.value) || 0)}
                  className="bg-slate-800 border-slate-700"
                  data-testid="product-min-stock"
                />
              </div>

              {!isServiceMode && (
                <div className="col-span-2">
                  <Label className="text-slate-300">Fornecedor</Label>
                  <Select
                    value={formData.supplier_id || 'none'}
                    onValueChange={(value) => updateForm('supplier_id', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="product-supplier">
                      <SelectValue placeholder="Selecionar fornecedor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sem fornecedor definido</SelectItem>
                      {[...suppliers]
                        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt', { sensitivity: 'base' }))
                        .map((supplier) => (
                          <SelectItem key={supplier.id} value={supplier.id}>
                            {supplier.name}{supplier.nif ? ` · NIF ${supplier.nif}` : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-slate-500">
                    Usado para sugerir e validar a associação nas entradas de mercadoria.
                  </p>
                </div>
              )}

              {!isServiceMode && formData.supplier_id && (
                <div className="col-span-2">
                  <Label className="text-slate-300">Referência deste fornecedor</Label>
                  <Input value={formData.supplier_reference || ''}
                    onChange={(e) => updateForm('supplier_reference', e.target.value)}
                    className="bg-slate-800 border-slate-700" placeholder="Ex.: 00030102806001" />
                  <p className="mt-1 text-xs text-slate-500">Usada para associar automaticamente as linhas OCR deste fornecedor.</p>
                </div>
              )}

              {formData.cost_price > 0 && formData.sell_price > 0 && (
                <div className="col-span-2 p-3 bg-slate-800 rounded-lg">
                  <p className="text-sm text-slate-400">
                    Margem calculada: 
                    <span className="ml-2 font-bold text-emerald-400">
                      {((formData.sell_price - formData.cost_price) / formData.cost_price * 100).toFixed(1)}%
                    </span>
                  </p>
                </div>
              )}

              <div className="col-span-2">
                <Label className="text-slate-300">Descrição</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  className="bg-slate-800 border-slate-700"
                  data-testid="product-description"
                />
              </div>
            </div>

            <div className="rounded-xl border border-slate-700 bg-slate-800/40 p-4 space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-white">Serviço / Prestador</p>
                  <p className="text-xs text-slate-400">
                    Usa esta área para atos e serviços da categoria Medicina ou outros serviços sem stock.
                  </p>
                </div>

                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={!!formData.is_service}
                    onChange={(e) => updateForm('is_service', e.target.checked)}
                  />
                  É serviço
                </label>
              </div>

              {isServiceMode && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 rounded-lg border border-blue-800/40 bg-blue-950/20 px-3 py-2 text-xs text-blue-200">
                    Serviço ativo: este artigo será tratado sem controlo de stock/lotes. A taxa de IVA é definida individualmente na ficha do serviço.
                  </div>

                  <div>
                    <Label className="text-slate-300">Prestador por defeito</Label>
                    <Select
                      value={formData.default_provider_id || '__none__'}
                      onValueChange={(val) => updateForm('default_provider_id', val === '__none__' ? '' : val)}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700">
                        <SelectValue placeholder="Sem prestador por defeito" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Sem prestador por defeito</SelectItem>
                        {activeProviders.map((provider) => (
                          <SelectItem key={provider.id} value={provider.id}>
                            {provider.name}{provider.specialty ? ` · ${provider.specialty}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-slate-300">Regra de honorário</Label>
                    <Select
                      value={formData.fee_rule_type || 'none'}
                      onValueChange={(val) => updateForm('fee_rule_type', val)}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700">
                        <SelectValue placeholder="Sem regra" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem regra</SelectItem>
                        <SelectItem value="percent">Percentagem</SelectItem>
                        <SelectItem value="fixed">Valor fixo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-slate-300">
                      {formData.fee_rule_type === 'percent' ? 'Percentagem do prestador (%)' : 'Valor fixo por ato (€)'}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.fee_rule_value}
                      onChange={(e) => updateForm('fee_rule_value', parseFloat(String(e.target.value).replace(',', '.')) || 0)}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>

                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-slate-300">
                      <input
                        type="checkbox"
                        checked={!!formData.provider_required}
                        onChange={(e) => updateForm('provider_required', e.target.checked)}
                      />
                      Exigir seleção de prestador no POS
                    </label>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="border-slate-700"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600"
                data-testid="save-product-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPage;
